// ============================================================================
// CHANGO V5.0 — Ops Console Login (admin-only, optional 2FA)
// ============================================================================
// Pairs with backend `routes/ops/auth.js`. If the account has TOTP enabled,
// login is two-step. Otherwise password login completes immediately.
//
// After success, `useAuth().refresh()` repopulates the context with the admin
// identity and we route to /ops/dashboard. Admins carry `workspace_id=null`
// and `role='admin'`; every downstream `<RoleGate>` on ops pages demands
// `allow={['admin']}`.
// ============================================================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';

const credSchema = z.object({
  email: z.string().email('邮箱格式不正确'),
  password: z.string().min(1, '密码必填')
});

const otpSchema = z.object({
  otp_code: z.string().regex(/^\d{6}$/, '验证码为 6 位数字')
});

export default function OpsLogin() {
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [stage, setStage] = useState('creds'); // 'creds' | '2fa'
  const [challenge, setChallenge] = useState(null);
  const [errBanner, setErrBanner] = useState(null);

  const cred = useForm({ resolver: zodResolver(credSchema) });
  const otp = useForm({ resolver: zodResolver(otpSchema) });

  const onCreds = async (values) => {
    setErrBanner(null);
    try {
      const res = await api.post('/api/ops/login', values);
      if (res?.step === '2fa_required' && typeof res.challenge === 'string') {
        setChallenge(res.challenge);
        setStage('2fa');
      } else if (res?.subject_type === 'ops' || res?.access_token) {
        await refresh();
        navigate('/ops/dashboard', { replace: true });
      } else {
        setErrBanner('异常响应，请刷新后重试。');
      }
    } catch (err) {
      if (err instanceof HttpError) {
        if (err.code === 'ACCOUNT_LOCKED') {
          setErrBanner('账号已锁定（连续 3 次失败）。请 1 小时后重试或联系同伴解锁。');
        } else if (err.code === 'ADMIN_ACCOUNT_DISABLED') {
          setErrBanner('该管理员账号已被禁用。');
        } else if (err.code === 'ADMIN_2FA_REQUIRED') {
          setErrBanner('该账号需要先完成安全配置后才能登录。');
        } else {
          setErrBanner('登录失败：邮箱或密码错误。');
        }
      } else {
        setErrBanner('网络错误，请稍后重试。');
      }
    }
  };

  const onOtp = async (values) => {
    setErrBanner(null);
    try {
      await api.post('/api/ops/login/2fa', {
        challenge,
        otp_code: values.otp_code
      });
      await refresh();
      navigate('/ops/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof HttpError) {
        if (err.code === 'INVALID_CHALLENGE') {
          setErrBanner('挑战已过期，请重新输入密码。');
          setStage('creds');
          setChallenge(null);
        } else {
          setErrBanner('验证码错误，请重新输入。');
          otp.reset({ otp_code: '' });
        }
      } else {
        setErrBanner('网络错误，请稍后重试。');
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-lg">
        <div className="mb-6 text-center">
          <h1 className="text-lg font-semibold text-slate-900">Ops 控制台登录</h1>
          <p className="mt-1 text-xs text-slate-500">
            CHANGO V5.0 · 管理员专用 · 可选 2FA
          </p>
        </div>

        {errBanner && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {errBanner}
          </div>
        )}

        {stage === 'creds' ? (
          <form onSubmit={cred.handleSubmit(onCreds)} className="space-y-4">
            <div>
              <label className="label" htmlFor="ops-email">邮箱</label>
              <input
                id="ops-email"
                type="email"
                className="field"
                autoComplete="username"
                {...cred.register('email')}
              />
              {cred.formState.errors.email && (
                <p className="field-error">{cred.formState.errors.email.message}</p>
              )}
            </div>
            <div>
              <label className="label" htmlFor="ops-password">密码</label>
              <input
                id="ops-password"
                type="password"
                className="field"
                autoComplete="current-password"
                {...cred.register('password')}
              />
              {cred.formState.errors.password && (
                <p className="field-error">{cred.formState.errors.password.message}</p>
              )}
            </div>
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={cred.formState.isSubmitting}
            >
              {cred.formState.isSubmitting ? '验证中…' : '下一步'}
            </button>
          </form>
        ) : (
          <form onSubmit={otp.handleSubmit(onOtp)} className="space-y-4">
            <p className="text-sm text-slate-600">
              请输入认证器应用中的 6 位动态码。
            </p>
            <div>
              <label className="label" htmlFor="ops-otp">动态码</label>
              <input
                id="ops-otp"
                type="text"
                inputMode="numeric"
                maxLength={6}
                autoComplete="one-time-code"
                className="field text-center font-mono text-lg tracking-[0.5em]"
                {...otp.register('otp_code')}
              />
              {otp.formState.errors.otp_code && (
                <p className="field-error">{otp.formState.errors.otp_code.message}</p>
              )}
            </div>
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                className="btn-ghost text-sm"
                onClick={() => { setStage('creds'); setChallenge(null); }}
              >
                返回
              </button>
              <button
                type="submit"
                className="btn-primary flex-1"
                disabled={otp.formState.isSubmitting}
              >
                {otp.formState.isSubmitting ? '验证中…' : '登录'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
